import { Show } from 'solid-js'

import { app } from '../../../../.configs/app/environment.js'

/**
 * StaticSVG - the same `icons/normalized/*` vocabulary `custom-svg.jsx` serves, resolved EAGERLY so
 * the icon exists in server-rendered markup.
 *
 * `CustomSVG` resolves its glob through `lazy()` inside a `createEffect`. Neither runs on the
 * server, so every icon it renders is absent from a prerendered page: the location landing pages
 * shipped `<span class="location-neighbourhoods__pin"></span>` — a bare green circle where the map
 * pin should have been — and nothing filled it, because that section is not one of the hydrated
 * islands. [doc: run log, Phase 8c]
 *
 * Eager costs the bundle every icon the glob matches, which is why the glob is the NORMALIZED
 * folder alone (the curated 24x24 `currentColor` set) rather than `assets/**`. Reach for
 * `CustomSVG` anywhere a browser is guaranteed; reach for this one on a surface that is prerendered.
 *
 * Under the unit runner there is no bundler, so `import.meta.glob` is shimmed to return nothing and
 * this renders nothing — the icon is proved by the build, not by the SSR harness.
 * [doc: tests/_helpers/jsx-ssr-loader.mjs]
 *
 * A name that is not in the vocabulary used to render an empty `<Show>` and say nothing about it,
 * which is how a mistyped icon reaches a page looking like a deliberate blank. It now throws
 * everywhere but production — see `reportUnknownIcon` for why that is the split, and for why an
 * EMPTY registry is not an unknown icon.
 */
const NORMALIZED_ICON_DIRECTORY = '/public/assets/icons/normalized'

const normalizedIcons = import.meta.glob('/public/assets/icons/normalized/*.svg', {
    eager: true,
    import: 'default',
    query: '?component-solid',
})

/**
 * Whether the icon vocabulary loaded at all.
 *
 * An empty registry means there was no bundler to run the glob — the unit runner's shim — not that
 * the requested icon is unknown. Every name is absent there, so the unknown-name check has to stand
 * down or the SSR harness fails on markup the build renders correctly.
 * [doc: tests/_helpers/jsx-ssr-loader.mjs]
 */
const hasIconRegistry = Object.keys(normalizedIcons).length > 0

/**
 * An icon name the vocabulary does not carry: loud in development, quiet in production.
 *
 * The prerender and the dev server both run outside `production`, so a bad name fails the landing
 * build and the dev page rather than shipping a hole in the layout. In a production bundle the
 * missing glyph is not worth taking a page — or an island's hydration — down for, so it is reported
 * and the element renders nothing, which is what this did unconditionally before.
 *
 * @param {string} name
 * @throws {Error} Outside production.
 */
function reportUnknownIcon (name) {
    const knownNames = Object.keys(normalizedIcons)
        .map((iconPath) => iconPath.slice(`${NORMALIZED_ICON_DIRECTORY}/`.length).replace(/\.svg$/u, ''))
        .sort() // ascending, so the list reads the way the folder does
    const message = `StaticSVG: no icon named "${name}" in ${NORMALIZED_ICON_DIRECTORY}. `
        + `Known names: ${knownNames.join(', ')}`

    console.error(message)

    if (!app.isProd) {
        throw new Error(message)
    }
}

/**
 * @param {object} props
 * @param {string} props.name - File name inside `public/assets/icons/normalized`, without `.svg`.
 * @param {string} [props.class]
 */
export default function StaticSVG (props) {
    const iconComponent = () => {
        const icon = normalizedIcons[`${NORMALIZED_ICON_DIRECTORY}/${props.name}.svg`]

        if (!icon && hasIconRegistry) {
            reportUnknownIcon(props.name)
        }

        return icon
    }

    return (
        <Show when={iconComponent()} keyed>
            {(IconComponent) => <IconComponent class={props.class} aria-hidden="true" />}
        </Show>
    )
}
